<template>
  <div class="container-fluid">
    <div class="page-header-title">
      <h2 class="mb-0">
        {{ $t('databank.title') }}
      </h2>
     
    </div>

    <!-- Alert Messages -->
    <div v-if="error" class="alert alert-danger alert-dismissible fade show" role="alert">
      {{ error }}
      <button type="button" class="btn-close" @click="error = null"></button>
    </div>
    
    <div v-if="success" class="alert alert-success alert-dismissible fade show" role="alert">
      {{ success }}
      <button type="button" class="btn-close" @click="success = null"></button>
    </div>

    <div class="row">
      <!-- Certificates Databank -->
      <div class="col-lg-6 col-xl-4 mb-4">
        <div class="card h-100">
          <div class="card-body d-flex flex-column justify-content-between">
            <div class="text-center text-muted">
              <i class="fas fa-file-certificate fa-3x mb-3 opacity-50"></i>
              <h6 class="mb-2">{{ $t('databank.certificates.title') }}</h6>
              <p class="small mb-3">{{ $t('databank.certificates.description') }}</p>
            </div>
            <div class="d-grid">
              <router-link to="/databank/certificates" class="btn btn-outline-primary btn-sm">
                {{ $t('databank.certificates.manage') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Status Management -->
      <div class="col-lg-6 col-xl-4 mb-4">
        <div class="card h-100">
          <div class="card-body d-flex flex-column justify-content-between">
            <div class="text-center text-muted">
              <i class="fas fa-flag fa-3x mb-3 opacity-50"></i>
              <h6 class="mb-2">{{ $t('superAdmin.statuses.title') }}</h6>
              <p class="small mb-3">{{ $t('databank.statuses.description') || 'Manage app statuses and their properties' }}</p>
            </div>
            <div class="d-grid">
              <router-link to="/super-admin/statuses" class="btn btn-outline-primary btn-sm">
                {{ $t('databank.statuses.manage') || 'Manage' }}
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- Mail Template -->
      <div class="col-lg-6 col-xl-4 mb-4">
        <div class="card h-100">
          <div class="card-body d-flex flex-column justify-content-between">
            <div class="text-center text-muted">
              <i class="fas fa-envelope fa-3x mb-3 opacity-50"></i>
              <h6 class="mb-2">{{ $t('databank.mailTemplate.title') }}</h6>
              <p class="small mb-3">{{ $t('databank.mailTemplate.description') }}</p>
            </div>
            <div class="d-grid">
              <router-link to="/databank/mail-template" class="btn btn-outline-primary btn-sm">
                {{ $t('databank.mailTemplate.manage') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- PDF Template Management (Super Admin) -->
      <div class="col-lg-6 col-xl-4 mb-4">
        <div class="card h-100">
          <div class="card-body d-flex flex-column justify-content-between">
            <div class="text-center text-muted">
              <i class="fas fa-file-pdf fa-3x mb-3 opacity-50"></i>
              <h6 class="mb-2">{{ $t('databank.pdfTemplate.title') || 'PDF Template' }}</h6>
              <p class="small mb-3">
                {{ $t('databank.pdfTemplate.description') || 'Manage reusable PDF templates for system documents.' }}
              </p>
            </div>
            <div class="d-grid">
              <router-link to="/pdf-templates" class="btn btn-outline-primary btn-sm">
                {{ $t('databank.pdfTemplate.manage') || 'Manage PDF Templates' }}
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <!-- User Guide -->
      <div class="col-lg-6 col-xl-4 mb-4">
        <div class="card h-100">
          <div class="card-body d-flex flex-column justify-content-between">
            <div class="text-center text-muted">
              <i class="fas fa-book fa-3x mb-3 opacity-50"></i>
              <h6 class="mb-2">{{ $t('databank.userGuide.title') }}</h6>
              <p class="small mb-3">{{ $t('databank.userGuide.description') }}</p>
            </div>
            <div class="d-grid">
              <router-link to="/databank/user-guide" class="btn btn-outline-primary btn-sm">
                {{ $t('databank.userGuide.manage') }}
              </router-link>
            </div>
          </div>
        </div>
      </div>

      <div class="col-lg-6 col-xl-4 mb-4">
        <div class="card h-100 border-dashed">
          <div class="card-body d-flex flex-column justify-content-center align-items-center">
            <div class="text-center text-muted">
              <i class="fas fa-plus-circle fa-3x mb-3 opacity-50"></i>
              <h6 class="mb-2">{{ $t('databank.comingSoon.title') }}</h6>
              <p class="small mb-0">{{ $t('databank.comingSoon.description') }}</p>
            </div>
          </div>
        </div>
      </div>
    </div>

   
  
  </div>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue';
import { useStore } from 'vuex';
import { useRouter } from 'vue-router';
import { useI18n } from 'vue-i18n';

const store = useStore();
const router = useRouter();
const { t } = useI18n();

const error = ref(null);
const success = ref(null);
const isLoadingStats = ref(false);

const systemStats = ref({
  certificateTypes: 0,
  activeCertificates: 0,
  totalRecords: 0,
  lastUpdated: ''
});

const currentUser = computed(() => store.getters['auth/currentUser']);

onMounted(() => {
  // Check if user is super admin
  if (!currentUser.value?.isSuperAdmin) {
    store.dispatch('notify', {
      message: t('common.notification.unauthorized'),
      type: 'error'  
    });
    router.push('/admin');
  }
  
  // Load system statistics
  loadSystemStats();
});

const loadSystemStats = async () => {
  isLoadingStats.value = true;
  
  try {
    // Simulate API call for system statistics
    await new Promise(resolve => setTimeout(resolve, 1000));
    
    systemStats.value = {
      certificateTypes: 15,
      activeCertificates: 245,
      totalRecords: 1250,
      lastUpdated: new Date().toLocaleDateString()
    };
    
  } catch (err) {
    console.error('Error loading system stats:', err);
    error.value = t('databank.errors.loadStatsError');
  } finally {
    isLoadingStats.value = false;
  }
};
</script>

<style scoped>
.page-header-title {
  margin-bottom: 2rem;
}

.card {
  border: none;
  border-radius: 12px;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.1);
  transition: transform 0.2s ease-in-out;
}

.card:hover:not(.border-dashed) {
  transform: translateY(-2px);
}

.card-header {
  background: white;
  color: white;
  border-radius: 12px 12px 0 0 !important;
  border: none;
}

.border-dashed {
  border: 2px dashed #e1e5e9 !important;
  background-color: #f8f9fa;
}

.btn {
  border-radius: 8px;
  font-weight: 500;
}

.alert {
  border-radius: 8px;
  border: none;
}

.text-primary {
  color: #667eea !important;
}

.text-success {
  color: #28a745 !important;
}

.text-info {
  color: #17a2b8 !important;
}

.text-warning {
  color: #ffc107 !important;
}

.opacity-50 {
  opacity: 0.5;
}
</style> 